import { useState } from 'react';
import { toast } from 'sonner';
import { CalendarCheck, Check, CreditCard, Lock, Package, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero, Reveal } from '../components/Reveal';
import LeadForm from '../components/LeadForm';
import { CONSULTATION_INCLUDES, PLANS } from '../data/content';
import { CONTACT, PLAN_LINKS } from '../data/config';

const STEPS = [
  { n: '01', icon: Package, label: 'Choose Package' },
  { n: '02', icon: CreditCard, label: 'Pay Securely' },
  { n: '03', icon: CalendarCheck, label: 'Pick Your Time' },
];

const BookConsultation = () => {
  const [planId, setPlanId] = useState('roadmap');
  const [paid, setPaid] = useState(false);
  const plan = PLANS.find((p) => p.id === planId);
  const planMeta = PLAN_LINKS[planId];

  const confirmPaid = () => {
    setPaid(true);
    toast.success('Great! Now pick a time for your session.');
    document.querySelector('[data-testid=book-step-time]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <main data-testid="book-consultation-page">
      <SEO
        title="Book a Career Consultation | CareerWithRaaj"
        description="Book a one-to-one career consultation in 3 steps: choose your package, pay securely via Razorpay, and pick your time."
      />
      <PageHero
        overline="Book Consultation"
        title="Book Your Session in 3 Simple Steps"
        description="Choose your package, pay securely, and then pick a time that works for you. Your session is confirmed once payment is verified."
      />

      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <div className="grid gap-3 sm:grid-cols-3" data-testid="booking-steps-indicator">
              {STEPS.map((s) => {
                const Icon = s.icon;
                const active = s.n === '03' ? paid : true;
                return (
                  <div
                    key={s.n}
                    className={`flex items-center gap-3 rounded-2xl border px-5 py-4 transition-colors duration-300 ${
                      active ? 'border-coral/40 bg-coral/5' : 'border-line bg-mist'
                    }`}
                    data-testid={`booking-step-indicator-${s.n}`}
                  >
                    <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${active ? 'bg-coral text-white' : 'bg-white text-slate-400'}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Step {s.n}</p>
                      <p className={`text-sm font-bold ${active ? 'text-navy' : 'text-slate-400'}`}>{s.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>

          <div className="mt-10 grid items-start gap-6 lg:grid-cols-3">
            <Reveal>
              <div className="h-full rounded-3xl border border-line bg-white p-7 shadow-card" data-testid="book-step-package">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-coral">01 — Choose Your Package</p>
                <div className="mt-5 space-y-3">
                  {PLANS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => {
                        setPlanId(p.id);
                        setPaid(false);
                      }}
                      data-testid={`package-option-${p.id}`}
                      aria-pressed={planId === p.id}
                      className={`w-full rounded-2xl border p-4 text-left transition-[border-color,background-color,box-shadow] duration-200 ${
                        planId === p.id ? 'border-coral bg-coral/5 ring-2 ring-coral/25' : 'border-line bg-white hover:border-navy/30'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-bold text-navy">{p.name}</p>
                        <p className="font-display text-lg font-extrabold text-coral">{PLAN_LINKS[p.id].price}</p>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{p.for}</p>
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl border border-line bg-white p-7 shadow-card" data-testid="book-step-payment">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-coral">02 — Pay Securely</p>
                <p className="mt-5 text-sm leading-relaxed text-slate-600">
                  Pay <span className="font-bold text-navy">{planMeta.price}</span> for your{' '}
                  <span className="font-bold text-navy">{plan.name}</span> via Razorpay — UPI, cards, netbanking, and
                  wallets accepted.
                </p>
                <a
                  href={planMeta.paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pay-now-button"
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-coral py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-coral-deep"
                >
                  Pay {planMeta.price} via Razorpay <ArrowRight className="h-4 w-4" />
                </a>
                <button
                  onClick={confirmPaid}
                  data-testid="payment-done-button"
                  className="mt-3 w-full rounded-full border-2 border-navy/15 py-3 text-sm font-semibold text-navy transition-[border-color,color] duration-200 hover:border-coral hover:text-coral"
                >
                  I've Completed the Payment
                </button>
                <p className="mt-4 text-center text-xs text-slate-400">
                  Payment opens in a secure Razorpay page. Come back here and confirm once done.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div
                className={`h-full rounded-3xl border p-7 transition-colors duration-300 ${
                  paid ? 'border-coral/40 bg-navy shadow-card-hover' : 'border-line bg-mist'
                }`}
                data-testid="book-step-time"
              >
                <p className={`text-xs font-bold uppercase tracking-[0.2em] ${paid ? 'text-coral' : 'text-slate-400'}`}>
                  03 — Pick Your Time
                </p>
                {paid ? (
                  <>
                    <p className="mt-5 text-sm leading-relaxed text-white/80">
                      Payment noted — now choose a time slot that works for you. We verify every payment before the
                      session.
                    </p>
                    {CONTACT.calendarLink ? (
                      <a
                        href={CONTACT.calendarLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="calendly-book-button"
                        className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-coral py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-coral-deep"
                      >
                        Pick Your Time <CalendarCheck className="h-4 w-4" />
                      </a>
                    ) : (
                      <p className="mt-5 rounded-xl bg-white/10 p-4 text-sm text-white/80">
                        We'll reach out within one working day to schedule your session.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="mt-5 flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-10 text-center" data-testid="time-selection-locked">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-400 shadow-card">
                      <Lock className="h-5 w-5" />
                    </span>
                    <p className="mt-4 text-sm font-semibold text-slate-500">Time selection is locked</p>
                    <p className="mt-1 text-xs text-slate-400">Choose your package and complete payment to unlock.</p>
                  </div>
                )}
              </div>
            </Reveal>
          </div>

          <div className="mt-24 grid items-start gap-14 lg:grid-cols-2">
            <div>
              <Reveal>
                <div className="mb-8 flex items-center gap-4 rounded-2xl border border-line bg-mist p-5" data-testid="guide-intro-card">
                  <img
                    src="/raaj.jpg"
                    alt="Raaj — Founder & Career Guide, CareerWithRaaj"
                    className="h-14 w-14 rounded-full object-cover ring-2 ring-coral/30"
                    data-testid="guide-photo"
                  />
                  <div>
                    <p className="text-sm font-bold text-navy">Your guide: Raaj</p>
                    <p className="text-xs text-slate-500">Founder & Career Guide, CareerWithRaaj</p>
                  </div>
                </div>
              </Reveal>
              <Reveal>
                <h2 className="font-display text-2xl font-bold text-navy md:text-3xl">What Your Session Includes</h2>
              </Reveal>
              <ul className="mt-8 space-y-4">
                {CONSULTATION_INCLUDES.map((item, i) => (
                  <Reveal key={item} delay={0.05 * i}>
                    <li className="flex items-center gap-4 rounded-2xl border border-line bg-mist px-5 py-4" data-testid={`consult-include-${i + 1}`}>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-coral/10">
                        <Check className="h-4 w-4 text-coral" />
                      </span>
                      <span className="text-sm font-semibold text-navy md:text-base">{item}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>

            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-line bg-white p-8 shadow-card-hover md:p-10" data-testid="consultation-form-card">
                <h2 className="font-display text-2xl font-bold text-navy">Have a Question First?</h2>
                <p className="mt-2 text-sm text-slate-500">
                  Not sure which package fits? Share your details and we'll call you back to help you decide.
                </p>
                <div className="mt-7">
                  <LeadForm source="consultation" showMessage buttonLabel="Request a Callback" interestLabel="Area of Interest" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BookConsultation;
